import React, { useState, useEffect } from 'react';
import { MoreHorizontal, GitBranch, FileText, Anchor, MessageSquare, ChevronDown } from 'lucide-react';
import { getRequest } from '../../services/apiService'; // Assuming apiService.js has a postRequest method
import { postRequest } from '../../services/apiService';

const NavalConflictsResolve = () => {
  const [showDropdown, setShowDropdown] = useState({ incoming: false, current: false });
  const [conflicts, setConflicts] = useState([]); // Will store fetched conflicts
  const [loadingConflicts, setLoadingConflicts] = useState(false);
  const [resolvedConflicts, setResolvedConflicts] = useState({});
  const [selectedRows, setSelectedRows] = useState({});
  const [remarks, setRemarks] = useState({});
  const [showRemarkInput, setShowRemarkInput] = useState({});
  const [apiResolutions, setApiResolutions] = useState([]); // New state to store data for API payload

  // New state for dropdowns
  const [projects, setProjects] = useState([]);
  const [ships, setShips] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedShip, setSelectedShip] = useState('');
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingShips, setLoadingShips] = useState(false);
  const [isResolving, setIsResolving] = useState(false); // To manage loading state for API call


  // Fetch projects on component mount
  useEffect(() => {
    fetchProjects();
  }, []);

  // Fetch ships when project is selected
  useEffect(() => {
    if (selectedProject) {
      fetchShips(selectedProject);
      setSelectedShip(''); // Reset ship selection when project changes
    } else {
      setShips([]);
      setSelectedShip('');
    }
  }, [selectedProject]);

  // Fetch conflicts when a ship is selected
  useEffect(() => {
    if (selectedShip) {
      fetchConflicts(selectedShip);
    } else {
      setConflicts([]);
      setApiResolutions([]); // Clear resolutions when ship changes
    }
  }, [selectedShip]);

  const fetchProjects = async () => {
    setLoadingProjects(true);
    try {
      const response = await getRequest('master/projects/');
      if (response.status === 'success') {
        setProjects(response.data);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoadingProjects(false);
    }
  };

  const fetchShips = async (projectId) => {
    setLoadingShips(true);
    try {
      const response = await getRequest(`master/projects/?parent_id=${projectId}`);
      if (response.status === 'success') {
        setShips(response.data);
      }
    } catch (error) {
      console.error('Error fetching ships:', error);
    } finally {
      setLoadingShips(false);
    }
  };

  const fetchConflicts = async (shipId) => {
    setLoadingConflicts(true);
    try {
      const response = await getRequest(`/pert/conflicts/${shipId}/?resolution_status=pending`);
      if (response.conflicts) {
        // Transform the fetched conflicts into the structure expected by the component
        const transformedConflicts = response.conflicts.map(c => ({
          id: c.conflict_id,
          lineStart: c.conflict_id, // Using conflict_id for lineStart for simplicity
          incoming: {
            content: `| Field Name | Value |\n|------------|-------|\n| ${c.field_name} | ${c.incoming_value} |`,
            description: `Incoming update for task: ${c.task_name}`,
            rawValue: c.incoming_value // Store raw value for API payload
          },
          current: {
            content: `| Field Name | Value |\n|------------|-------|\n| ${c.field_name} | ${c.existing_value} |`,
            description: `Current value for task: ${c.task_name}`,
            rawValue: c.existing_value // Store raw value for API payload
          },
          fieldName: c.field_name // Store field name for API payload
        }));
        setConflicts(transformedConflicts);
        setApiResolutions([]); // Reset API resolutions when new conflicts are fetched
      }
    } catch (error) {
      console.error('Error fetching conflicts:', error);
    } finally {
      setLoadingConflicts(false);
    }
  };

  const handleProjectChange = (e) => {
    setSelectedProject(e.target.value);
  };

  const handleShipChange = (e) => {
    setSelectedShip(e.target.value);
  };

  const toggleDropdown = (panel) => {
    setShowDropdown(prev => ({
      ...prev,
      [panel]: !prev[panel]
    }));
  };

  const handleAction = (action, panel) => {
    console.log(`${action} clicked for ${panel} panel`);
    setShowDropdown(prev => ({
      ...prev,
      [panel]: false
    }));
  };

  const toggleRowSelection = (conflictId, rowIndex, source) => {
    const key = `${conflictId}-${rowIndex}`;
    setSelectedRows(prev => ({
      ...prev,
      [key]: prev[key] === source ? null : source
    }));
  };

  const handleConflictResolution = (conflictId, resolutionType) => {
    const conflict = conflicts.find(c => c.id === conflictId);
    let resolvedContent = '';
    let action = '';
    let mergedValue = null;
    
    switch(resolutionType) {
      case 'accept-incoming':
        resolvedContent = conflict.incoming.content;
        action = 'override';
        mergedValue = conflict.incoming.rawValue;
        break;
      case 'accept-current':
        resolvedContent = conflict.current.content;
        action = 'keep_existing';
        mergedValue = conflict.current.rawValue;
        break;
      case 'row-selection':
      default:
        const incomingRows = parseTableRows(conflict.incoming.content);
        const currentRows = parseTableRows(conflict.current.content);
        const headers = parseTableHeaders(conflict.incoming.content);
        
        let resolvedRows = [];
        const maxRows = Math.max(incomingRows.length, currentRows.length);
        
        // For row selection, we assume manual_merge and construct merged_value
        const selectedValues = [];
        for (let i = 0; i < maxRows; i++) {
          const key = `${conflictId}-${i}`;
          const selectedSource = selectedRows[key];
          
          if (selectedSource === 'incoming' && incomingRows[i]) {
            resolvedRows.push(incomingRows[i]);
            selectedValues.push(incomingRows[i][1]); // Assuming value is at index 1
          } else if (selectedSource === 'current' && currentRows[i]) {
            resolvedRows.push(currentRows[i]);
            selectedValues.push(currentRows[i][1]); // Assuming value is at index 1
          } else if (incomingRows[i]) {
            resolvedRows.push(incomingRows[i]); // Default to incoming if no selection
            selectedValues.push(incomingRows[i][1]);
          } else if (currentRows[i]) {
            resolvedRows.push(currentRows[i]);
            selectedValues.push(currentRows[i][1]);
          }
        }
        
        resolvedContent = buildTableContent(headers, resolvedRows);
        action = 'manual_merge';
        // For simplicity, if multiple rows, merge values with a delimiter or handle as needed
        // Here, we'll assume a single row for simplicity or join for multi-row
        if (selectedValues.length === 1) {
          mergedValue = selectedValues[0];
        } else if (selectedValues.length > 1) {
          // This part needs careful consideration based on what 'merged_value' should be
          // for a multi-row table resolution in your API.
          // For now, let's join them, but you might need a more sophisticated merge logic.
          mergedValue = selectedValues.join(';'); 
        } else {
          mergedValue = null; // No rows selected/resolved
        }
        break;
    }
    
    setResolvedConflicts(prev => ({
      ...prev,
      [conflictId]: {
        type: resolutionType,
        content: resolvedContent,
        remark: remarks[conflictId] || ''
      }
    }));

    setApiResolutions(prev => {
      const existingResolutionIndex = prev.findIndex(res => res.conflict_id === conflictId);
      const newResolution = {
        conflict_id: conflictId,
        action: action,
        merged_value: mergedValue,
        comment: remarks[conflictId] || ''
      };

      if (existingResolutionIndex > -1) {
        const updatedResolutions = [...prev];
        updatedResolutions[existingResolutionIndex] = newResolution;
        return updatedResolutions;
      } else {
        return [...prev, newResolution];
      }
    });
    
    // Remove the resolved conflict from the list
    setConflicts(prevConflicts => prevConflicts.filter(c => c.id !== conflictId));
  };

  const parseTableHeaders = (content) => {
    const lines = content.split('\n');
    const headerLine = lines.find(line => line.trim().startsWith('|'));
    return headerLine ? headerLine.split('|').map(h => h.trim()).filter(h => h) : [];
  };

  const parseTableRows = (content) => {
    const lines = content.split('\n');
    const tableLines = lines.filter(line => line.trim().startsWith('|'));
    return tableLines.slice(2).map(line => 
      line.split('|').map(cell => cell.trim()).filter(cell => cell)
    );
  };

  const buildTableContent = (headers, rows) => {
    const headerRow = '| ' + headers.join(' | ') + ' |';
    const separatorRow = '|' + headers.map(() => '--------').join('|') + '|';
    const dataRows = rows.map(row => '| ' + row.join(' | ') + ' |');
    
    return [headerRow, separatorRow, ...dataRows].join('\n');
  };

  const handleRemarkChange = (conflictId, value) => {
    setRemarks(prev => ({
      ...prev,
      [conflictId]: value
    }));
    // Also update the apiResolutions if a remark is added/changed for an already resolved conflict
    setApiResolutions(prev => {
      return prev.map(res => 
        res.conflict_id === conflictId ? { ...res, comment: value } : res
      );
    });
  };

  const toggleRemarkInput = (conflictId) => {
    setShowRemarkInput(prev => ({
      ...prev,
      [conflictId]: !prev[conflictId]
    }));
  };

  const handleResolveAllConflicts = async () => {
    setIsResolving(true);
    try {
      const payload = {
        resolutions: apiResolutions
      };
      const response = await postRequest('pert/conflicts/resolve/', payload);
      if (response.status === 'success') {
        alert('Conflicts resolved successfully!');
        // Optionally re-fetch conflicts or update UI to reflect resolution
        fetchConflicts(selectedShip); // Re-fetch to clear resolved ones
        setResolvedConflicts({}); // Clear local resolved state
        setApiResolutions([]); // Clear API resolutions
      } else {
        alert('Failed to resolve conflicts: ' + response.message);
      }
    } catch (error) {
      console.error('Error resolving conflicts:', error);
      alert('Error resolving conflicts.');
    } finally {
      setIsResolving(false);
    }
  };


  const DropdownMenu = ({ panel, show }) => (
    show && (
      <div className="absolute right-0 top-8 bg-gray-100 border border-gray-300 rounded shadow-lg z-10 min-w-32">
        <button 
          onClick={() => handleAction('Accept All', panel)}
          className="w-full px-4 py-2 text-left text-gray-800 hover:bg-gray-200 text-sm"
        >
          Accept All
        </button>
        <button 
          onClick={() => handleAction('Reject All', panel)}
          className="w-full px-4 py-2 text-left text-gray-800 hover:bg-gray-200 text-sm"
        >
          Reject All
        </button>
      </div>
    )
  );

  const renderSelectableTable = (content, conflictId, source) => {
    const lines = content.split('\n');
    const tableLines = lines.filter(line => line.trim().startsWith('|'));
    
    if (tableLines.length === 0) return <pre>{content}</pre>; // Render as preformatted text if not a table
    
    const headers = tableLines[0].split('|').map(h => h.trim()).filter(h => h);
    const rows = tableLines.slice(2).map(line => 
      line.split('|').map(cell => cell.trim()).filter(cell => cell)
    );
    
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs border-collapse border border-gray-300">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-300 px-2 py-1 w-8">Select</th>
              {headers.map((header, index) => (
                <th key={index} className="border border-gray-300 px-2 py-1 text-left font-medium">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => {
              const key = `${conflictId}-${rowIndex}`;
              const isSelected = selectedRows[key] === source;
              const isOtherSelected = selectedRows[key] && selectedRows[key] !== source;
              
              return (
                <tr 
                  key={rowIndex} 
                  className={`${
                    isSelected ? 'bg-green-100' : 
                    isOtherSelected ? 'bg-red-50' :
                    rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                  } ${isOtherSelected ? 'opacity-50' : ''}`}
                >
                  <td className="border border-gray-300 px-2 py-1 text-center">
                    <input
                      type="radio"
                      name={`conflict-${conflictId}-row-${rowIndex}`}
                      checked={isSelected}
                      onChange={() => toggleRowSelection(conflictId, rowIndex, source)}
                      className="text-green-600"
                    />
                  </td>
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex} className="border border-gray-300 px-2 py-1">
                      {cell}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderResolvedTable = (content) => {
    const lines = content.split('\n');
    const tableLines = lines.filter(line => line.trim().startsWith('|'));
    
    if (tableLines.length === 0) return <pre>{content}</pre>;
    
    const headers = tableLines[0].split('|').map(h => h.trim()).filter(h => h);
    const rows = tableLines.slice(2).map(line => 
      line.split('|').map(cell => cell.trim()).filter(cell => cell)
    );
    
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs border-collapse border border-gray-300">
          <thead>
            <tr className="bg-gray-100">
              {headers.map((header, index) => (
                <th key={index} className="border border-gray-300 px-2 py-1 text-left font-medium">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                {row.map((cell, cellIndex) => (
                  <td key={cellIndex} className="border border-gray-300 px-2 py-1">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderConflictSection = (conflict, type) => {
    const isResolved = resolvedConflicts[conflict.id];
    const data = type === 'incoming' ? conflict.incoming : conflict.current;
    
    return (
      <div key={conflict.id} className="mb-4">
        <div className={`${type === 'incoming' ? 'bg-green-50 border-green-300' : 'bg-blue-50 border-blue-300'} border p-3 rounded`}>
          <div className="text-xs text-gray-600 mb-2 font-medium">{data.description}</div>
          
          {!isResolved && (
            <div className="mb-3">
              <div className="flex flex-wrap gap-1 mb-2">
                <button 
                  onClick={() => handleConflictResolution(conflict.id, type === 'incoming' ? 'accept-incoming' : 'accept-current')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded text-xs transition-colors"
                >
                  Accept {type === 'incoming' ? 'Incoming' : 'Current'}
                </button>
                {/* Removed "Accept Combination" buttons */}
                {/* Removed "Ignore" button */}
                <button 
                  onClick={() => toggleRemarkInput(conflict.id)}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white px-2 py-1 rounded text-xs transition-colors flex items-center gap-1"
                >
                  <MessageSquare size={12} />
                  Add Remark
                </button>
              </div>
              
              {/* Removed "Resolve with Row Selection" button and descriptive text */}
              <div className="mb-2">
                <button 
                  onClick={() => handleConflictResolution(conflict.id, 'row-selection')}
                  className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-xs transition-colors"
                >
                  Resolve with Row Selection
                </button>
                <span className="text-xs text-gray-500 ml-2">Select individual rows above, then click to apply</span>
              </div>
              
              {showRemarkInput[conflict.id] && (
                <div className="mb-2">
                  <textarea
                    value={remarks[conflict.id] || ''}
                    onChange={(e) => handleRemarkChange(conflict.id, e.target.value)}
                    placeholder="Add resolution remark..."
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded resize-none"
                    rows="2"
                  />
                </div>
              )}
            </div>
          )}
          
          {isResolved && (
            <div className="text-green-600 text-xs mb-2 font-medium">
              ✓ Resolved - Row Selection Applied
              {resolvedConflicts[conflict.id].remark && (
                <div className="text-gray-600 mt-1 italic">
                  Remark: {resolvedConflicts[conflict.id].remark}
                </div>
              )}
            </div>
          )}
          
          <div className="mt-2">
            {!isResolved ? 
              renderSelectableTable(data.content, conflict.id, type) :
              renderResolvedTable(data.content)
            }
          </div>
        </div>
      </div>
    );
  };

  const conflictsRemaining = conflicts.length;
  const allConflictsResolved = conflictsRemaining === 0 && Object.keys(resolvedConflicts).length > 0; // Check if all were resolved

  return (
    <div className="bg-gray-50 text-gray-900 font-sans text-sm h-screen flex flex-col">
      {/* Top Filter Section */}
      <div className="bg-white border-b border-gray-300 p-4">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Project
            </label>
            <div className="relative">
              <select
                value={selectedProject}
                onChange={handleProjectChange}
                disabled={loadingProjects}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
              >
                <option value="">
                  {loadingProjects ? 'Loading projects...' : 'Choose a project'}
                </option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
          
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Ship
            </label>
            <div className="relative">
              <select
                value={selectedShip}
                onChange={handleShipChange}
                disabled={!selectedProject || loadingShips}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8 disabled:bg-gray-100"
              >
                <option value="">
                  {!selectedProject 
                    ? 'Select a project first' 
                    : loadingShips 
                    ? 'Loading ships...' 
                    : ships.length === 0
                    ? 'No ships available'
                    : 'Choose a ship'
                  }
                </option>
                {ships.map((ship) => (
                  <option key={ship.id} value={ship.id}>
                    {ship.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {selectedProject && selectedShip && (
            <div className="flex-1">
              <div className="text-sm text-gray-600">
                <div className="font-medium">Selected Configuration:</div>
                <div className="text-xs">
                <span>  Project: {projects.find(p => p.id.toString() === selectedProject)?.name}</span>
                 <span> Ship: {ships.find(s => s.id.toString() === selectedShip)?.name}</span>
               
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Top panels - always visible with fixed height */}
      {(conflictsRemaining > 0 || loadingConflicts || Object.keys(resolvedConflicts).length > 0) && ( // Show panels if there are conflicts or resolved ones
        <div className="flex h-96 border-b border-gray-300">
          {/* Incoming Panel */}
          <div className="flex-1 border-r border-gray-300 flex flex-col">
            <div className="bg-blue-100 px-4 py-2 border-b border-gray-300 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Anchor size={16} className="text-blue-600" />
                <span className="text-gray-700 font-medium">Incoming</span>
                <span className="text-blue-600 font-semibold">ind-547d9</span>
                <span className="bg-green-500 text-white px-2 py-1 rounded text-xs">origin/indian-navy-updates, indian-navy-updates</span>
              </div>
              <div className="relative">
                <button 
                  onClick={() => toggleDropdown('incoming')}
                  className="text-gray-600 hover:text-gray-800 p-1"
                >
                  <MoreHorizontal size={16} />
                </button>
                <DropdownMenu panel="incoming" show={showDropdown.incoming} />
              </div>
            </div>
            
            <div className="flex-1 overflow-auto bg-white">
              <div className="p-4">
                <div className="text-gray-600 mb-2 flex items-center">
                  <FileText size={16} className="inline mr-2" />
                  pert-conflicts.md
                </div>
                <div className="text-gray-500 mb-4">Pending PERT conflicts from incoming changes</div>
                
                <div className="space-y-1">
                  {loadingConflicts ? (
                    <div className="text-gray-500">Loading conflicts...</div>
                  ) : conflicts.length > 0 ? (
                    conflicts.map(conflict => renderConflictSection(conflict, 'incoming'))
                  ) : (
                    <div className="text-gray-500">No incoming conflicts for selected ship.</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Current Panel */}
          <div className="flex-1 flex flex-col">
            <div className="bg-orange-100 px-4 py-2 border-b border-gray-300 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Anchor size={16} className="text-orange-600" />
                <span className="text-gray-700 font-medium">Current</span>
                <span className="text-orange-600 font-semibold">ind-403d</span>
                <span className="bg-blue-500 text-white px-2 py-1 rounded text-xs">main, origin/main</span>
              </div>
              <div className="relative">
                <button 
                  onClick={() => toggleDropdown('current')}
                  className="text-gray-600 hover:text-gray-800 p-1"
                >
                  <MoreHorizontal size={16} />
                </button>
                <DropdownMenu panel="current" show={showDropdown.current} />
              </div>
            </div>
            
            <div className="flex-1 overflow-auto bg-white">
              <div className="p-4">
                <div className="text-gray-600 mb-2 flex items-center">
                  <FileText size={16} className="inline mr-2" />
                  pert-conflicts.md
                </div>
                <div className="text-gray-500 mb-4">Current PERT data (stable)</div>
                
                <div className="space-y-1">
                  {loadingConflicts ? (
                    <div className="text-gray-500">Loading conflicts...</div>
                  ) : conflicts.length > 0 ? (
                    conflicts.map(conflict => renderConflictSection(conflict, 'current'))
                  ) : (
                    <div className="text-gray-500">No current conflicts for selected ship.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Result Panel */}
      <div className={`${conflictsRemaining > 0 || loadingConflicts || Object.keys(resolvedConflicts).length > 0 ? 'h-96' : 'flex-1'} flex flex-col`}>
        <div className="bg-green-100 px-4 py-2 border-b border-gray-300 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={`${conflictsRemaining > 0 ? 'text-red-600 bg-red-100' : 'text-green-600 bg-green-200'} px-2 py-1 rounded text-xs font-medium`}>
              {conflictsRemaining > 0 ? 'Merge Result' : 'Resolved'}
            </span>
            <Anchor size={16} className="text-green-600" />
            <span className="text-gray-700 font-medium">resolved-pert-data.md</span>
          </div>
          <div className="flex items-center gap-4">
            <span className={`${conflictsRemaining > 0 ? 'text-orange-600' : 'text-green-600'} text-xs font-medium`}>
              {loadingConflicts ? 'Loading conflicts...' : `${conflictsRemaining} PERT Conflicts Remaining`}
            </span>
            <button 
              onClick={handleResolveAllConflicts}
              disabled={!allConflictsResolved || isResolving}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                !allConflictsResolved || isResolving
                  ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
            >
              {isResolving ? 'Resolving...' : 'Resolve Conflicts'}
            </button>
            <button className="text-gray-600 hover:text-gray-800 p-1">
              <MoreHorizontal size={16} />
            </button>
          </div>
        </div>
        
        <div className="flex-1 overflow-auto bg-white">
          <div className="p-4">
            <div className="text-gray-600 mb-4">
              <span>{conflictsRemaining > 0 ? "Select individual rows from each table to resolve conflicts. Use radio buttons to choose the preferred version for each row." : "All PERT conflicts for the selected ship have been resolved successfully."}</span>
            </div>
            <div className="text-gray-500 mb-4">PERT conflict resolution status</div>
            
            <div className="space-y-4">
              {Object.keys(resolvedConflicts).length > 0 ? (
                Object.values(resolvedConflicts).map((resolution, index) => (
                  <div key={index} className="mb-4">
                    <div className="bg-green-50 border border-green-300 p-3 rounded">
                      <div className="text-green-600 text-xs mb-2 font-medium">
                        ✓ PERT Conflict Resolved - {resolution.type === 'row-selection' ? 'Row Selection Applied' : resolution.type.replace('-', ' ')}
                      </div>
                      {resolution.remark && (
                        <div className="text-gray-600 text-xs mb-2 italic">
                          Remark: {resolution.remark}
                        </div>
                      )}
                      <div className="mt-2">
                        {renderResolvedTable(resolution.content)}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                !loadingConflicts && conflicts.length === 0 && selectedShip && (
                  <div className="text-gray-500">No conflicts to display for this ship.</div>
                )
              )}

              {conflicts.map(conflict => {
                const resolution = resolvedConflicts[conflict.id];
                if (!resolution) { // Only render if not yet resolved
                  return (
                    <div key={conflict.id} className="mb-4">
                      <div className="bg-yellow-50 border border-yellow-300 p-3 rounded">
                        <div className="text-orange-600 text-xs mb-2 font-medium">
                          ⚠ PERT Conflict {conflict.id} - Awaiting Row Selection
                        </div>
                        <div className="text-gray-600 text-sm">
                          Task: {conflicts.find(c => c.id === conflict.id)?.incoming.description.replace('Incoming update for task: ', '') || 'N/A'} - Select rows from incoming and current versions above
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NavalConflictsResolve;